import { useEffect, useState, useMemo } from 'react';
import { adminApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import DataTable from '../../components/ui/DataTable.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Select from '../../components/ui/Select.jsx';
import Modal from '../../components/ui/Modal.jsx';
import Badge from '../../components/ui/Badge.jsx';

const currentYear = String(new Date().getFullYear());

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

export default function HolidaysPage() {
  const toast = useToast();
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [yearFilter, setYearFilter] = useState(currentYear);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [importing, setImporting] = useState(false);
  const [dedupePreview, setDedupePreview] = useState(null); // { totalGroups, totalToRemove, groups }
  const [dedupeBusy, setDedupeBusy] = useState(false);
  const [weekendPreview, setWeekendPreview] = useState(null); // { totalToShift, plan }
  const [weekendBusy, setWeekendBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const res = await adminApi.listHolidays(yearFilter);
      setHolidays(res.holidays || []);
    } catch (err) {
      toast.error(`Failed to load holidays: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [yearFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleImport(pack) {
    const packLabel = pack.replace('_', ' ');
    if (!window.confirm(`Import ${packLabel} holidays?`)) return;
    setImporting(true);
    try {
      const res = await adminApi.importHolidays(pack);
      const parts = [];
      if (res.imported) parts.push(`${res.imported} added`);
      if (res.skipped) parts.push(`${res.skipped} already on file`);
      toast.success(`${packLabel}: ${parts.join(', ') || `${res.total || 0} processed`}`);
      await load();
    } catch (err) {
      toast.error(`Import failed: ${err.message}`);
    } finally {
      setImporting(false);
    }
  }

  async function openDedupePreview() {
    setDedupeBusy(true);
    try {
      const res = await adminApi.previewHolidayDupes();
      setDedupePreview(res);
    } catch (err) {
      toast.error(`Couldn't load duplicates: ${err.message}`);
    } finally {
      setDedupeBusy(false);
    }
  }

  async function confirmDedupe() {
    setDedupeBusy(true);
    try {
      const res = await adminApi.runHolidayDedupe();
      const errStr = res.errors?.length ? `, ${res.errors.length} failed` : '';
      toast.success(`Removed ${res.deleted} duplicate holiday row${res.deleted === 1 ? '' : 's'}${errStr}`);
      setDedupePreview(null);
      await load();
    } catch (err) {
      toast.error(`Cleanup failed: ${err.message}`);
    } finally {
      setDedupeBusy(false);
    }
  }

  async function openWeekendPreview() {
    setWeekendBusy(true);
    try {
      const res = await adminApi.previewWeekendShift();
      setWeekendPreview(res);
    } catch (err) {
      toast.error(`Couldn't load weekend shifts: ${err.message}`);
    } finally {
      setWeekendBusy(false);
    }
  }

  async function confirmWeekendShift() {
    setWeekendBusy(true);
    try {
      const res = await adminApi.runWeekendShift();
      const errStr = res.errors?.length ? `, ${res.errors.length} failed` : '';
      const skipStr = res.skipped ? `, ${res.skipped} skipped (no free workday)` : '';
      toast.success(`Shifted ${res.shifted} weekend holiday${res.shifted === 1 ? '' : 's'} to the next workday${skipStr}${errStr}`);
      setWeekendPreview(null);
      await load();
    } catch (err) {
      toast.error(`Shift failed: ${err.message}`);
    } finally {
      setWeekendBusy(false);
    }
  }

  const filtered = useMemo(() => {
    if (!search) return holidays;
    const s = search.toLowerCase();
    return holidays.filter(
      (h) =>
        h.name?.toLowerCase().includes(s) ||
        h.date?.includes(s) ||
        h.countryCode?.toLowerCase().includes(s)
    );
  }, [holidays, search]);

  const columns = [
    {
      key: 'date',
      label: 'Date',
      render: (h) => (
        <div>
          <div className="font-medium">{formatDate(h.date)}</div>
          <div className="text-xs text-muted-foreground">{h.date}</div>
        </div>
      ),
    },
    { key: 'name', label: 'Name' },
    {
      key: 'type',
      label: 'Type',
      render: (h) => (
        <Badge variant={h.type === 'half_day' ? 'warning' : 'default'}>
          {h.type === 'half_day' ? 'Half day' : 'Full day'}
        </Badge>
      ),
    },
    {
      key: 'countryCode',
      label: 'Country',
      render: (h) => h.countryCode?.toUpperCase() || '—',
    },
    {
      key: 'isRecurring',
      label: 'Recurring',
      render: (h) => (
        <Badge variant={h.isRecurring ? 'success' : 'default'}>
          {h.isRecurring ? 'Yes' : 'No'}
        </Badge>
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight mb-2">Holidays</h1>
          <p className="text-muted-foreground">
            {holidays.length} holidays in {yearFilter}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={openWeekendPreview} loading={weekendBusy}>
            Shift weekend → workday
          </Button>
          <Button variant="ghost" onClick={openDedupePreview} loading={dedupeBusy}>
            Remove duplicates
          </Button>
          <Button variant="secondary" onClick={() => handleImport(`KSA_${yearFilter}`)} loading={importing}>
            Import KSA {yearFilter}
          </Button>
          <Button variant="secondary" onClick={() => handleImport(`US_${yearFilter}`)} loading={importing}>
            Import US {yearFilter}
          </Button>
          <Button onClick={() => setEditingHoliday({})}>Add holiday</Button>
        </div>
      </div>

      <div className="flex gap-3 mb-5 items-end">
        <Input
          label="Search"
          placeholder="Search holidays..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 max-w-[400px]"
        />
        <Select
          label="Year"
          value={yearFilter}
          onChange={(e) => setYearFilter(e.target.value)}
          options={[
            { value: '2025', label: '2025' },
            { value: '2026', label: '2026' },
            { value: '2027', label: '2027' },
          ]}
        />
      </div>

      <DataTable
        columns={columns}
        rows={filtered}
        loading={loading}
        emptyMessage={
          holidays.length === 0
            ? 'No holidays for this year. Import a starter pack or add holidays manually.'
            : 'No holidays match your search.'
        }
        onRowClick={(h) => setEditingHoliday(h)}
        keyFn={(h) => h.rowKey}
      />

      {editingHoliday !== null && (
        <HolidayEditModal
          holiday={editingHoliday}
          onClose={() => setEditingHoliday(null)}
          onSaved={async () => {
            setEditingHoliday(null);
            await load();
          }}
        />
      )}

      {weekendPreview && (
        <Modal
          isOpen
          onClose={() => !weekendBusy && setWeekendPreview(null)}
          title="Shift weekend holidays to the next workday"
          width="640px"
          footer={
            <>
              <Button variant="ghost" onClick={() => setWeekendPreview(null)} disabled={weekendBusy}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={confirmWeekendShift}
                loading={weekendBusy}
                disabled={weekendPreview.totalToShift === 0}
              >
                Shift {weekendPreview.totalToShift} holiday{weekendPreview.totalToShift === 1 ? '' : 's'}
              </Button>
            </>
          }
        >
          {weekendPreview.totalToShift === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No KSA holidays currently sit on Fri/Sat — nothing to shift.
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Saudi practice grants observed in-lieu days when a holiday lands on the weekend.
                These <strong className="text-foreground">{weekendPreview.totalToShift}</strong>{' '}
                row{weekendPreview.totalToShift === 1 ? '' : 's'} will move from Fri/Sat to the
                next available KSA workday (Sun → Mon → …), and the new row gets the{' '}
                <em>(observed)</em> suffix.
              </p>
              <div className="max-h-[320px] overflow-y-auto rounded-md border border-border bg-muted/30">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Holiday</th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">From</th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">→ To</th>
                    </tr>
                  </thead>
                  <tbody>
                    {weekendPreview.plan.map((p) => (
                      <tr key={p.rowKey} className="border-t border-border/50">
                        <td className="px-3 py-2 font-medium">{p.name}</td>
                        <td className="px-3 py-2 font-mono text-xs text-destructive">{p.date}</td>
                        <td className="px-3 py-2 font-mono text-xs text-success">
                          {p.targetDate || <span className="text-muted-foreground italic">no free workday</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground">
                Each move deletes the Fri/Sat row, creates a new "(observed)" row on the target
                date, and cleans up any linked group-calendar event.
              </p>
            </div>
          )}
        </Modal>
      )}

      {dedupePreview && (
        <Modal
          isOpen
          onClose={() => !dedupeBusy && setDedupePreview(null)}
          title="Remove duplicate holidays"
          width="640px"
          footer={
            <>
              <Button variant="ghost" onClick={() => setDedupePreview(null)} disabled={dedupeBusy}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={confirmDedupe}
                loading={dedupeBusy}
                disabled={dedupePreview.totalToRemove === 0}
              >
                Remove {dedupePreview.totalToRemove} duplicate{dedupePreview.totalToRemove === 1 ? '' : 's'}
              </Button>
            </>
          }
        >
          {dedupePreview.totalGroups === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No duplicates found — your holiday list is clean.
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Found <strong className="text-foreground">{dedupePreview.totalGroups}</strong>{' '}
                duplicate group{dedupePreview.totalGroups === 1 ? '' : 's'}. Will keep the oldest
                row in each group and remove{' '}
                <strong className="text-foreground">{dedupePreview.totalToRemove}</strong> extra
                row{dedupePreview.totalToRemove === 1 ? '' : 's'}. Linked group-calendar events
                are removed too.
              </p>
              <div className="max-h-[320px] overflow-y-auto rounded-md border border-border bg-muted/30">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Date</th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Holiday</th>
                      <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Copies → kept</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dedupePreview.groups.map((g) => (
                      <tr key={g.key} className="border-t border-border/50">
                        <td className="px-3 py-2 font-mono text-xs">{g.date}</td>
                        <td className="px-3 py-2">
                          <div className="font-medium">{g.name}</div>
                          {g.countryCode && (
                            <div className="text-xs text-muted-foreground">{g.countryCode}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right text-xs">
                          <span className="text-destructive font-medium">{g.totalCount}</span>{' '}
                          → <span className="text-success font-medium">1</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground">
                This action is final. Take a moment to confirm the list above looks right.
              </p>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

function HolidayEditModal({ holiday, onClose, onSaved }) {
  const toast = useToast();
  const isNew = !holiday.rowKey;

  const [form, setForm] = useState({
    date: holiday.date || '',
    name: holiday.name || '',
    type: holiday.type || 'full_day',
    isRecurring: holiday.isRecurring || false,
    countryCode: holiday.countryCode || '',
  });
  const [saving, setSaving] = useState(false);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleSave() {
    if (!form.date) {
      toast.error('Date is required');
      return;
    }
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setSaving(true);
    try {
      await adminApi.upsertHoliday({
        id: holiday.rowKey || undefined,
        date: form.date,
        name: form.name.trim(),
        type: form.type,
        isRecurring: form.isRecurring,
        countryCode: form.countryCode.trim(),
      });
      toast.success(isNew ? `Added "${form.name}"` : `Updated "${form.name}"`);
      onSaved();
    } catch (err) {
      toast.error(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Delete "${holiday.name}" (${holiday.date})? This cannot be undone.`)) return;
    setSaving(true);
    try {
      await adminApi.deleteHoliday(holiday.partitionKey, holiday.rowKey);
      toast.success(`Deleted "${holiday.name}"`);
      onSaved();
    } catch (err) {
      toast.error(`Delete failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={isNew ? 'Add holiday' : `Edit ${holiday.name}`}
      width="480px"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          {!isNew && (
            <Button variant="danger" onClick={handleDelete} loading={saving}>
              Delete
            </Button>
          )}
          <Button onClick={handleSave} loading={saving}>
            {isNew ? 'Add holiday' : 'Save changes'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label="Date"
          type="date"
          value={form.date}
          onChange={(e) => set('date', e.target.value)}
        />
        <Input
          label="Holiday name"
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
          placeholder="e.g. National Day"
        />
        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Type"
            value={form.type}
            onChange={(e) => set('type', e.target.value)}
            options={[
              { value: 'full_day', label: 'Full day' },
              { value: 'half_day', label: 'Half day' },
            ]}
          />
          <Input
            label="Country code"
            value={form.countryCode}
            onChange={(e) => set('countryCode', e.target.value)}
            placeholder="e.g. KSA, US"
          />
        </div>
        <label className="flex items-center gap-2.5 rounded-md border border-border bg-card p-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.isRecurring}
            onChange={(e) => set('isRecurring', e.target.checked)}
          />
          <div>
            <div className="text-sm font-medium">Recurring annually</div>
            <div className="text-xs text-muted-foreground">
              This holiday repeats on the same date every year.
            </div>
          </div>
        </label>
      </div>
    </Modal>
  );
}
